/**
 * What stops a journal posting or an entry reversing, in words, with the one
 * thing to do about it; and the dialog that opens a lock exception for the
 * person before trying again.
 */
import { useState, type JSX } from "react";
import { Link } from "@tanstack/react-router";
import { Alert, Button, Dialog, Textarea, nonEmpty } from "@linkhr/design-system";
import { intlLocale, useTranslation } from "@linkhr/i18n";
import { useOpenLockException } from "../api/journals";
import { useRoleMappings } from "../api/ledger";
import { PAYMENT_STALE_REASONS } from "../api/codes";
import { codeLabel } from "../components/codeLabel";
import { useLedgerFormat } from "../ledger/format";
import { useViewMode } from "../shell/useViewMode";
import {
  GENERIC,
  exceptionEndsOn,
  isOperation,
  labelList,
  ledgerErrorParams,
  messageParams,
  problemText,
  readRefusal,
  roleFix,
  type Action,
  type Operation,
  type Problem,
} from "./refusal";

/** Codes the banner's action already speaks for. */
const ACTION_CODES = new Set([
  "ACCT_ROLE_NOT_MAPPED",
  "ACCT_FX_RATE_MISSING",
  "ACCT_BEFORE_BOOKS_START",
  "ACCT_PERIOD_HARD_CLOSED",
  "ACCT_PERIOD_SOFT_CLOSED",
]);

/** The exceptions to open, one per operation, `operation` first. */
export interface ExceptionAsk {
  periodId: string;
  operation: Operation;
  more?: readonly Operation[];
}

/**
 * The operations' labels as one list ("posting documents and matching open items"); in
 * Business view a bank adjustment reads as the owner's money in and out.
 */
export function useOperationList(business = false): (operations: readonly string[]) => string {
  const { t, i18n } = useTranslation("accounting");
  const list = new Intl.ListFormat(intlLocale(i18n.language), { type: "conjunction" });
  return (operations) =>
    list.format(
      operations.map((op) =>
        business && op === "BANK_ADJUSTMENT"
          ? t("accounting:ledger.journal.exception.operation_business.BANK_ADJUSTMENT")
          : codeLabel(i18n, t, `accounting:ledger.journal.exception.operation.${op}`, op),
      ),
    );
}

export function RefusalBanner({
  companyId,
  action,
  problems,
  currency,
  onReload,
  onException,
  onTryAgain,
  onUseDate,
  fxHref,
  rolesHref,
  args = {},
}: {
  companyId: string;
  action: Action | null;
  problems: readonly Problem[];
  currency: string;
  onReload?: () => void;
  onException: (ask: ExceptionAsk) => void;
  onTryAgain: () => void;
  onUseDate: (date: string) => void;
  /** Where exchange rates are entered, for someone who may enter them. */
  fxHref: string | null;
  /** Where default accounts are set, for someone who may set them. */
  rolesHref: string | null;
  /** The refusal's own facts, for the words its sentence names. */
  args?: Record<string, unknown>;
}): JSX.Element | null {
  const { t, i18n } = useTranslation("accounting");
  const f = useLedgerFormat();
  // A date no month covers is the banner's own sentence only when it leads; behind another action it stays listed.
  const spokenFor = (code: string): boolean =>
    code === "ACCT_PERIOD_NOT_FOUND"
      ? action?.kind === "use_date" && action.reason === "NOT_FOUND"
      : action !== null && ACTION_CODES.has(code);
  const entry = problems.filter((problem) => problem.lineNo === null && !spokenFor(problem.code));
  const onLines = problems.some((problem) => problem.lineNo !== null);
  const { mode } = useViewMode(companyId);
  const operations = useOperationList(mode === "business");
  // Read only while a missing default account shows: whether it's locked decides who sets it.
  const mappings = useRoleMappings(companyId, action?.kind === "support");

  const banner = ((): JSX.Element | null => {
    if (action === null) return null;
    switch (action.kind) {
      case "stale":
        return (
          <Alert
            variant="warning"
            description={
              <span className="flex flex-wrap items-center gap-sm">
                {t(
                  (PAYMENT_STALE_REASONS as readonly (string | null)[]).includes(action.reason)
                    ? `accounting:payments.stale.${String(action.reason)}`
                    : "accounting:ledger.journal.stale",
                  { name: action.name ?? t("accounting:ledger.journal.someone") },
                )}
                {onReload !== undefined ? (
                  <Button size="sm" variant="secondary" onClick={onReload}>
                    {t("accounting:ledger.journal.reload")}
                  </Button>
                ) : null}
              </span>
            }
          />
        );
      case "exception": {
        // Both buttons, not Try again alone: the server sets viewerHasException only when the
        // viewer holds an exception for a DIFFERENT operation (PostingEngine.admit, backend), so a
        // retry alone fails again; releasing needs POST_DOCUMENT and ALLOCATE, each its own exception.
        const [first, ...more] = action.operations.filter(isOperation);
        const open = action.canOpen && first !== undefined;
        const named = operations(
          action.operations.length > 0 ? action.operations : [action.operation],
        );
        return (
          <Alert
            variant="warning"
            description={
              <span className="flex flex-wrap items-center gap-sm">
                {/* Someone who can't open one is told to ask, Try again or not (a database race). */}
                {open || (action.canOpen && action.tryAgain)
                  ? t("accounting:ledger.journal.exception.needed", { operation: named })
                  : t("accounting:ledger.journal.exception.ask", { operation: named })}
                {open ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      onException({
                        periodId: action.periodId,
                        operation: first,
                        ...(more.length > 0 ? { more } : {}),
                      })
                    }
                  >
                    {t("accounting:ledger.journal.exception.open")}
                  </Button>
                ) : null}
                {action.tryAgain ? (
                  <Button size="sm" variant="ghost" onClick={onTryAgain}>
                    {t("accounting:ledger.journal.exception.try_again")}
                  </Button>
                ) : null}
              </span>
            }
          />
        );
      }
      case "use_date": {
        const date = action.date;
        const uncovered = problems.find((problem) => problem.code === "ACCT_PERIOD_NOT_FOUND");
        return (
          <Alert
            variant="danger"
            description={
              <span className="flex flex-wrap items-center gap-sm">
                {action.reason === "HARD_CLOSED"
                  ? t("accounting:ledger.journal.closed")
                  : action.reason === "NOT_FOUND" && uncovered !== undefined
                    ? problemText(f, t, i18n, uncovered, currency)
                    : t("accounting:ledger.journal.before_start", {
                        date: date !== null ? f.day(date) : "",
                      })}
                {date !== null ? (
                  <Button size="sm" variant="secondary" onClick={() => onUseDate(date)}>
                    {t("accounting:ledger.journal.use_date", { date: f.day(date) })}
                  </Button>
                ) : null}
              </span>
            }
          />
        );
      }
      case "fx":
        return (
          <Alert
            variant="danger"
            description={
              <span className="flex flex-wrap items-center gap-sm">
                {t("accounting:ledger.problems.ACCT_FX_RATE_MISSING", {
                  currency: action.currency,
                  date: action.date !== "" ? f.day(action.date) : "",
                })}
                {fxHref !== null ? (
                  <Link to={fxHref} className="text-primary underline focus-visible:focus-ring">
                    {t("accounting:ledger.journal.fx_link")}
                  </Link>
                ) : null}
              </span>
            }
          />
        );
      case "support": {
        const fix = roleFix(action, mappings.data, rolesHref !== null);
        return (
          <Alert
            variant="danger"
            description={
              <span className="flex flex-wrap items-center gap-sm">
                {action.roles.length > 0
                  ? t("accounting:ledger.journal.role_missing", {
                      roles: labelList(
                        f,
                        t,
                        i18n,
                        "accounting:ledger.account_roles",
                        action.roles,
                        "conjunction",
                      ),
                    })
                  : t("accounting:ledger.journal.role_missing_any")}
                {fix === "link" && rolesHref !== null ? (
                  <Link to={rolesHref} className="text-primary underline focus-visible:focus-ring">
                    {t("accounting:ledger.journal.roles_link")}
                  </Link>
                ) : fix === "ask" ? (
                  t("accounting:ledger.journal.roles_ask")
                ) : (
                  t("accounting:ledger.journal.role_support")
                )}
              </span>
            }
          />
        );
      }
      case "problems":
        return onLines ? (
          <Alert variant="danger" description={t("accounting:ledger.journal.fix_lines")} />
        ) : null;
      case "message": {
        const sentence = codeLabel(i18n, t, action.key, action.key, {
          ...messageParams(args),
          ...ledgerErrorParams(f, t, i18n, args, currency),
        });
        // A payment or a movement that moved on meanwhile says "Reload to see where it stands": the button does it.
        const reload =
          onReload !== undefined &&
          (action.key.startsWith("accounting:payments.error.ACCT_PAYMENT_WRONG_STATE") ||
            action.key.startsWith("accounting:banking.error.ACCT_BANK_MOVEMENT_STATE"));
        return (
          <Alert
            variant="danger"
            description={
              reload ? (
                <span className="flex flex-wrap items-center gap-sm">
                  {sentence}
                  <Button size="sm" variant="secondary" onClick={onReload}>
                    {t("accounting:ledger.journal.reload")}
                  </Button>
                </span>
              ) : (
                sentence
              )
            }
          />
        );
      }
    }
  })();

  if (banner === null && entry.length === 0) return null;
  return (
    <div className="flex flex-col gap-sm" role="status" aria-live="polite">
      {banner}
      {entry.length > 0 ? (
        <Alert
          variant="danger"
          title={t("accounting:ledger.journal.to_fix")}
          description={
            <ul className="list-disc ps-md">
              {entry.map((problem) => (
                <li key={`${problem.code}-${JSON.stringify(problem.args)}`}>
                  {problemText(f, t, i18n, problem, currency)}
                </li>
              ))}
            </ul>
          }
        />
      ) : null}
    </div>
  );
}

/**
 * A lock exception for the person themselves, for each operation the refusal
 * named (one reason, opened in turn), ending at the server's default; once all
 * are open, the caller tries again.
 */
export function ExceptionDialog({
  companyId,
  ask,
  today,
  onClose,
  onOpened,
}: {
  companyId: string;
  ask: ExceptionAsk | null;
  /** Today in the company's time zone. */
  today: string;
  onClose: () => void;
  onOpened: () => void;
}): JSX.Element {
  const { t, i18n } = useTranslation("accounting");
  const f = useLedgerFormat();
  const openException = useOpenLockException(companyId);
  const { mode } = useViewMode(companyId);
  const operations = useOperationList(mode === "business");
  const [reason, setReason] = useState("");
  const [failure, setFailure] = useState<string | null>(null);
  const all = ask === null ? [] : [ask.operation, ...(ask.more ?? [])];
  // How many of this ask are open already, kept across closing: asking again opens only the rest.
  const key = ask === null ? null : [ask.periodId, ...all].join(" ");
  const [progress, setProgress] = useState<{ key: string | null; opened: number }>({
    key: null,
    opened: 0,
  });
  const opened = progress.key === key ? progress.opened : 0;
  const close = (): void => {
    setReason("");
    setFailure(null);
    onClose();
  };
  const submit = async (): Promise<void> => {
    if (ask === null) return;
    try {
      for (const [index, operation] of all.entries()) {
        if (index < opened) continue;
        await openException.mutateAsync({
          periodId: ask.periodId,
          operation,
          reason: reason.trim(),
        });
        setProgress({ key, opened: index + 1 });
      }
      setProgress({ key: null, opened: 0 });
      close();
      onOpened();
    } catch (error) {
      const { action, problems } = readRefusal(error);
      const first = problems[0];
      setFailure(
        action.kind === "message"
          ? codeLabel(i18n, t, action.key, action.key)
          : first !== undefined
            ? problemText(f, t, i18n, first, "")
            : t(GENERIC),
      );
    }
  };
  return (
    <Dialog
      open={ask !== null}
      onOpenChange={(open) => {
        if (!open) close();
      }}
      title={nonEmpty(t("accounting:ledger.journal.exception.title"))}
      description={t("accounting:ledger.journal.exception.body", {
        operation: operations(all),
        date: f.day(exceptionEndsOn(today)),
      })}
      footer={
        <Button
          disabled={reason.trim() === "" || openException.isPending}
          onClick={() => void submit()}
        >
          {t("accounting:ledger.journal.exception.submit")}
        </Button>
      }
    >
      <span className="flex flex-col gap-xs">
        <Textarea
          aria-label={t("accounting:ledger.journal.exception.reason")}
          placeholder={t("accounting:ledger.journal.exception.reason")}
          rows={3}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {failure !== null ? (
          <span role="alert" className="text-label text-danger">
            {failure}
          </span>
        ) : null}
      </span>
    </Dialog>
  );
}
